// Operations Command Center (Aug 2026 Phase A + B).
//
// Premium operational hero rendered at the top of the admin Dashboard
// so an authorized user can immediately answer: what's happening now,
// where, who is teaching, which classes need attention. Everything
// below (the existing calendar / insights / quick actions) is
// unchanged — this component owns just the top strip.
//
// The single POST /dashboard/operations backend endpoint returns the
// entire DTO (KPIs, schedule, live/upcoming, alerts, per-location
// summary), scoped server-side to the authenticated user's location
// authorization. Non-admins cannot request a location they don't own —
// this component's location dropdown reflects the same allowed set.

import React, { useMemo, useState } from "react";
import {
  Box, Typography, Chip, Button,
  alpha, CircularProgress, Drawer, IconButton, Stack, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ChildCareOutlinedIcon from "@mui/icons-material/ChildCareOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FactCheckOutlinedIcon2 from "@mui/icons-material/PlaylistAddCheckOutlined";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  useGetDashboardOperationsQuery,
  useLazyGetDashboardClassRosterQuery,
} from "../../Api/attoDeskApi";
import { formatLateDurationCompact } from "../../utils/lateDuration";
import { summarizeClass } from "../../utils/classLabel";
import Tooltip from "@mui/material/Tooltip";
import { DashboardTintedCard } from "./DashboardTintedCard";
import { dashTokens, toneMap, toneForLocationIndex } from "./dashboardTheme";

// Muted premium palette (2026-08-24). `deepGreen` name preserved but
// repointed to steel-blue so every legacy consumer (banner, buttons,
// status pills, chips, quick-action icons) recolors in one edit.
// Semantic green stays only as `green` for success/live states.
const c = {
  navy: "#17324D", deepGreen: "#4E78A6", green: "#5BAA83",
  amber: "#B4934B", red: "#BF6973", blue: "#4E78A6",
  text: "#17324D", muted: "#7D8FA1", border: "#E2E8F0", card: "#FFFFFF",
  surfaceMint: "#F3F8F5", surfaceBlue: "#F2F6FA",
  surfaceAmber: "#FBF7ED", surfaceRed: "#FCF3F4",
  surfaceGrey: "#F3F5F8", surfaceNavy: "#EEF2F7",
};

const fmt12 = (t?: string | null) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

interface Props {
  firstName?: string;
  /** Shared dashboard location filter. FinanceOverview owns the
      visible selector; this component receives the same value so the
      finance and operations sections can never show different scopes
      at the same time. */
  locationId: number | "all";
  onLocationChange: (id: number | "all") => void;
}

const OperationsCommandCenter: React.FC<Props> = ({ firstName, locationId, onLocationChange }) => {
  const navigate = useNavigate();
  const setLocationId = onLocationChange;

  const { data, isLoading, isFetching, refetch } = useGetDashboardOperationsQuery(
    { location_id: locationId === "all" ? null : locationId },
    { pollingInterval: 60_000 },  // refresh every 60s so late alerts surface without a manual reload
  );
  const d: any = data?.data || null;

  const isAdmin: boolean = !!d?.scope?.is_admin;
  const availableLocations: { id: number; name: string }[] = d?.available_locations || [];
  const kpis = d?.kpis || {};
  const alerts: any[] = d?.alerts || [];
  const live: any[] = d?.live_classes || [];
  const upcoming: any[] = d?.upcoming_classes || [];
  const locationSummary: any[] = d?.location_summary || [];
  const graceMinutes: number = d?.grace_minutes || 5;
  const activeLocationName = useMemo(() => {
    if (locationId === "all") return "All Locations";
    return availableLocations.find((l) => l.id === locationId)?.name || "";
  }, [locationId, availableLocations]);

  // Show top-N alerts inline; the rest live behind the "View All"
  // drawer so the dashboard never becomes a 5-screen red wall.
  const ALERT_PREVIEW = 5;
  const [showAllAlerts, setShowAllAlerts] = useState(false);

  // Class Subjects modal — opened from the "View Subjects" chip on
  // alert / live / upcoming rows. Content is a modal (not a toast)
  // per operations spec: subject enumeration is *content*, not a
  // notification.
  const [subjectsOccId, setSubjectsOccId] = useState<number | null>(null);

  // Roster drawer (Students Expected).
  const [rosterOccId, setRosterOccId] = useState<number | null>(null);
  const [fetchRoster, { data: rosterResp, isFetching: rosterLoading }] = useLazyGetDashboardClassRosterQuery();
  const roster: any[] = (rosterResp?.data as any[]) || [];
  const openRoster = (occId: number) => { setRosterOccId(occId); fetchRoster({ occurrence_id: occId }); };
  const rosterOccurrence = useMemo(() => {
    if (!rosterOccId) return null;
    return live.find((o) => o.id === rosterOccId) || upcoming.find((o) => o.id === rosterOccId)
        || (d?.schedule || []).find((o: any) => o.id === rosterOccId) || null;
  }, [rosterOccId, live, upcoming, d?.schedule]);

  // Compact alert row used both inline (top-N) and in the View All
  // drawer. Redesigned Aug 2026:
  //   - white surface + 4px red left accent (not a full red row)
  //   - late badge on the left
  //   - class summary (compact or "N Grades · M Subjects" for complex)
  //   - **bold** teacher name
  //   - "View Subjects" is now a real button (soft mint) that opens
  //     the Class Subjects MODAL (never a toast — subject data is
  //     content, not a notification)
  //   - Students chip opens the roster drawer
  //   - View Class button navigates to the class edit page
  const renderAlertRow = (a: any, compact = false) => {
    const summary = summarizeClass(a.grade, a.subject);
    // Show the button whenever there IS any grade/subject to view,
    // even for simple single-mapping classes — spec: primary way to
    // inspect structured mappings must be a modal, not a tooltip.
    const hasSubjectDetail = summary.compact !== "Class" || (a.grade_subjects && a.grade_subjects.length > 0);

    return (
      <Box key={a.id} sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        px: compact ? 2 : 2.5, py: 1.5,
        borderLeft: `3px solid ${c.red}`,
        borderBottom: `1px solid ${c.border}`,
        bgcolor: c.card,
        "&:last-child": { borderBottom: "none" },
        transition: "all 150ms",
        "&:hover": { bgcolor: alpha(c.red, 0.02) },
      }}>
        {/* Red duration badge */}
        <Box sx={{
          minWidth: 92, textAlign: "center",
          bgcolor: c.surfaceRed, border: `1px solid ${alpha(c.red, 0.2)}`,
          borderRadius: "10px", px: 0.75, py: 0.5,
          flexShrink: 0,
        }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: c.red, lineHeight: 1.1 }}>
            {formatLateDurationCompact(a.minutes_late)}
          </Typography>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: c.red, textTransform: "uppercase", letterSpacing: "0.08em", mt: 0.25 }}>
            Late
          </Typography>
        </Box>

        {/* Class + teacher block */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <MenuBookOutlinedIcon sx={{ fontSize: 15, color: c.muted, flexShrink: 0 }} />
              <Tooltip title={summary.isComplex ? summary.detail : ""} placement="top" arrow>
                <Typography sx={{ fontSize: 13.75, fontWeight: 800, color: c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {summary.compact}
                </Typography>
              </Tooltip>
            </Box>
            {hasSubjectDetail && (
              <Button
                size="small"
                startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                onClick={() => setSubjectsOccId(a.id)}
                sx={{
                  textTransform: "none", fontWeight: 700, fontSize: 11,
                  borderRadius: "8px", minHeight: 26, py: 0, px: 1.25,
                  bgcolor: c.surfaceBlue, color: c.deepGreen,
                  border: `1px solid ${c.deepGreen}`,
                  "&:hover": { bgcolor: "#EAF2F8", borderColor: c.deepGreen },
                }}
              >
                View Subjects
              </Button>
            )}
          </Box>
          <Typography sx={{ fontSize: 11.5, color: c.muted, mt: 0.5 }}>
            Teacher:&nbsp;
            <Box component="span" sx={{ fontWeight: 800, color: c.text }}>
              {a.staff_name || "Unassigned"}
            </Box>
            {" · "}
            {a.location_name || "No location"}
            {" · "}
            {fmt12(a.start_time)}
          </Typography>
        </Box>

        {/* Students chip → opens roster drawer */}
        <Chip
          icon={<GroupsOutlinedIcon sx={{ fontSize: "14px !important" }} />}
          label={`${a.student_count} ${a.student_count === 1 ? "Student" : "Students"}`}
          size="small" clickable
          onClick={() => openRoster(a.id)}
          sx={{
            height: 28, fontSize: 11.5, fontWeight: 700,
            bgcolor: c.surfaceBlue, color: c.blue,
            border: `1px solid ${alpha(c.blue, 0.2)}`,
            flexShrink: 0,
            "&:hover": { bgcolor: alpha(c.blue, 0.12) },
          }}
        />
        {/* Late-start alert action: navigate to the exact occurrence's
            attendance page (not the recurring class's edit form). The
            occurrence id is `a.id` — `class_occurrences.id`. */}
        <Button
          size="small" variant="outlined"
          startIcon={<FactCheckOutlinedIcon2 sx={{ fontSize: "14px !important" }} />}
          onClick={() => navigate(`/class-sessions/${a.id}/attendance`)}
          sx={{
            textTransform: "none", fontSize: 11.5, fontWeight: 700,
            borderRadius: "8px", minHeight: 28, py: 0, px: 1.25,
            borderColor: alpha(c.navy, 0.25), color: c.navy, flexShrink: 0,
            "&:hover": { borderColor: c.navy, bgcolor: alpha(c.navy, 0.04) },
          }}
        >
          Open Attendance
        </Button>
      </Box>
    );
  };

  // Occurrence currently selected for the Class Subjects modal (looked
  // up from the payload so we don't need to plumb full class objects
  // through render — the id is enough).
  const subjectsOccurrence = useMemo(() => {
    if (!subjectsOccId) return null;
    return alerts.find((o) => o.id === subjectsOccId)
        || live.find((o) => o.id === subjectsOccId)
        || upcoming.find((o) => o.id === subjectsOccId)
        || (d?.schedule || []).find((o: any) => o.id === subjectsOccId)
        || null;
  }, [subjectsOccId, alerts, live, upcoming, d?.schedule]);

  const kpi = (label: string, value: React.ReactNode, hint: React.ReactNode, icon: React.ReactNode, tone: "green" | "blue" | "navy" | "amber" | "red") => {
    const color = tone === "green" ? c.green : tone === "amber" ? c.amber : tone === "red" ? c.red : tone === "blue" ? c.blue : c.navy;
    const surface = tone === "green" ? c.surfaceMint : tone === "amber" ? c.surfaceAmber : tone === "red" ? c.surfaceRed : tone === "blue" ? c.surfaceBlue : c.surfaceNavy;
    return (
      <Box sx={{
        bgcolor: c.card, border: `1px solid ${c.border}`, borderRadius: "14px",
        boxShadow: "0 1px 2px rgba(15,30,51,0.03), 0 4px 14px rgba(15,30,51,0.04)",
        p: 2, minWidth: 0,
        transition: "all 180ms",
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 2px 4px rgba(15,30,51,0.05), 0 8px 22px rgba(15,30,51,0.07)" },
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: surface, color, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 17 } }}>
            {icon}
          </Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {label}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: c.text, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
          {value}
        </Typography>
        {hint && <Typography sx={{ fontSize: 11.5, color: c.muted, mt: 0.5 }}>{hint}</Typography>}
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* ══════════ Hero ══════════ */}
      <Box sx={{
        position: "relative", overflow: "hidden",
        borderRadius: "22px", mb: 2.5,
        px: { xs: 2.5, md: 4 }, py: { xs: 2.5, md: 3 },
        color: "#fff",
        // Solid navy — matches the DailyBriefingBar hero for cross-
        // section visual consistency. Previous linear-gradient was
        // stripped in the 2026-09 Rainbow Colour Theory pass.
        bgcolor: c.navy,
        boxShadow: "0 10px 30px rgba(23,50,77,0.16)",
      }}>
        {/* Decorative bloom removed — the 2026-09 Full-Fill Rainbow
            v2 pass forbids floating decorative shapes on every
            dashboard surface, hero included. */}
        <Box sx={{ position: "relative", display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, opacity: 0.8, letterSpacing: "0.14em", textTransform: "uppercase" }}>
              Today at Step Up · {dayjs().format("dddd, MMMM D")}
            </Typography>
            <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, letterSpacing: "-0.02em", mt: 0.5, lineHeight: 1.15 }}>
              {greeting()}{firstName ? `, ${firstName}` : ""}
            </Typography>
            <Typography sx={{ fontSize: 13, opacity: 0.85, mt: 0.5 }}>
              {isAdmin ? "Operations command center — all authorized locations." : "Your operational view for the day."}
            </Typography>
          </Box>

          {/* Location pill (read-only mirror of the dashboard-wide
              location filter — the actual selector lives in the
              FinanceOverview above so the dashboard has exactly one
              location source of truth). Shows the current filter and
              a fetching spinner; changing it happens up there. */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1,
            bgcolor: alpha("#ffffff", 0.12),
            border: `1px solid ${alpha("#ffffff", 0.18)}`,
            borderRadius: "12px", px: 1.5, py: 0.75,
            backdropFilter: "blur(6px)",
          }}>
            <LocationOnIcon sx={{ fontSize: 16, opacity: 0.9 }} />
            <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>
              {activeLocationName}
            </Typography>
            {isFetching && <CircularProgress size={12} sx={{ color: "#fff", ml: 0.5 }} />}
          </Box>
        </Box>
      </Box>

      {/* ══════════ Operations KPI Row — 5 tinted cards per spec §43
          Classes Today = blue, Students Expected = green,
          Running Now = purple, Completed = teal,
          Needs Attention = red (auto-green when zero). */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" }, gap: 1.5, mb: 2.5 }}>
        <DashboardTintedCard
          tone="blue" icon={<CalendarTodayIcon />}
          label="Classes Today"
          value={isLoading ? "—" : (kpis.classes_today ?? 0)}
          hint={`${kpis.running ?? 0} running · ${kpis.remaining ?? 0} remaining`}
        />
        <DashboardTintedCard
          tone="green" icon={<GroupsOutlinedIcon />}
          label="Students Expected"
          value={isLoading ? "—" : (kpis.students_expected ?? 0)}
          hint={`${kpis.student_seats ?? 0} class seat${(kpis.student_seats ?? 0) === 1 ? "" : "s"}`}
        />
        <DashboardTintedCard
          tone="purple" icon={<BoltIcon />}
          label="Running Now"
          value={isLoading ? "—" : (kpis.running ?? 0)}
          hint={(kpis.running ?? 0) > 0 ? "Live sessions" : "No live classes"}
        />
        <DashboardTintedCard
          tone="teal" icon={<FactCheckOutlinedIcon />}
          label="Completed"
          value={isLoading ? "—" : (kpis.completed ?? 0)}
          hint={`of ${kpis.classes_today ?? 0} scheduled`}
        />
        <DashboardTintedCard
          tone={(kpis.needs_attention ?? 0) > 0 ? "rose" : "emerald"}
          icon={<WarningAmberIcon />}
          label="Needs Attention"
          value={isLoading ? "—" : (kpis.needs_attention ?? 0)}
          hint={(kpis.needs_attention ?? 0) > 0 ? `${graceMinutes}-min late threshold` : "Everything on time"}
        />
      </Box>

      {/* ══════════ Needs Attention (redesigned, Aug 2026)
          Compact operational summary. Shows only the top N most-urgent
          alerts inline; the full list opens in a right-side drawer so
          the dashboard never becomes a red wall. Each row uses:
            - red duration badge (formatLateDurationCompact)
            - compact class summary (summarizeClass) with tooltip on
              complex mappings for full grade/subject enumeration
            - BOLD teacher name (visual emphasis)
            - white surface + soft red tint + 4px red left border
          ═══════════════════════════════════════════════════════════ */}
      {alerts.length > 0 && (
        <Box sx={{
          // Spec §10 — Needs Attention container carries a clear rose
          // family identity (was white with a red border).
          bgcolor: toneMap.rose.grad,
          border: `1px solid ${toneMap.rose.border}`,
          borderRadius: "16px", overflow: "hidden", mb: 2.5,
          boxShadow: `0 1px 2px rgba(15,30,51,0.03), 0 4px 14px ${alpha(c.red, 0.08)}`,
        }}>
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 2.5, py: 1.5, gap: 1, flexWrap: "wrap",
            bgcolor: c.surfaceRed,
            borderBottom: `1px solid ${alpha(c.red, 0.15)}`,
            borderLeft: `4px solid ${c.red}`,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <WarningAmberIcon sx={{ color: c.red, fontSize: 20 }} />
              <Box>
                <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: c.red, lineHeight: 1.1 }}>
                  Needs Attention
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: c.red, opacity: 0.85, mt: 0.25 }}>
                  {alerts.length} class{alerts.length === 1 ? "" : "es"} need{alerts.length === 1 ? "s" : ""} review · showing top {Math.min(ALERT_PREVIEW, alerts.length)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip label={`Late Start ${alerts.length}`} size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: c.card, color: c.red, border: `1px solid ${alpha(c.red, 0.25)}` }} />
              <Chip label={`${graceMinutes}-min grace`} size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: c.card, color: c.muted, border: `1px solid ${c.border}` }} />
            </Box>
          </Box>
          <Box>
            {alerts.slice(0, ALERT_PREVIEW).map((a) => renderAlertRow(a, /*compact*/ true))}
          </Box>
          {alerts.length > ALERT_PREVIEW && (
            <Box sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              px: 2.5, py: 1.25, borderTop: `1px solid ${c.border}`, bgcolor: alpha(c.red, 0.02),
            }}>
              <Typography sx={{ fontSize: 12, color: c.muted }}>
                +{alerts.length - ALERT_PREVIEW} more not started
              </Typography>
              <Button size="small" onClick={() => setShowAllAlerts(true)}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: c.red }}>
                View all {alerts.length} alerts →
              </Button>
            </Box>
          )}
        </Box>
      )}

      {alerts.length === 0 && !isLoading && (kpis.classes_today ?? 0) > 0 && (
        <Box sx={{
          bgcolor: c.surfaceMint, border: `1px solid ${alpha(c.green, 0.2)}`,
          borderRadius: "14px", px: 2.5, py: 1.5, mb: 2.5,
          display: "flex", alignItems: "center", gap: 1.25,
        }}>
          <CheckCircleOutlineIcon sx={{ color: c.green }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: c.deepGreen }}>
            Everything is running on time at {activeLocationName}.
          </Typography>
        </Box>
      )}

      {/* ══════════ Location Summary (All Locations only) ══════════ */}
      {isAdmin && locationId === "all" && locationSummary.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1 }}>
            Locations Today
          </Typography>
          {/* Dynamic column count = min(actualLocations, breakpointCap).
              With 2 locations on desktop this renders as `repeat(2, ...)`,
              so the two cards expand to fill the row instead of leaving
              phantom empty tracks (spec §2). */}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: `repeat(${Math.min(locationSummary.length, 2)}, minmax(0, 1fr))`,
              md: `repeat(${Math.min(locationSummary.length, 3)}, minmax(0, 1fr))`,
              lg: `repeat(${Math.min(locationSummary.length, 4)}, minmax(0, 1fr))`,
            },
            gap: 1.5,
          }}>
            {locationSummary.map((loc, idx) => {
              const tone = toneForLocationIndex(idx);
              const t = toneMap[tone];
              return (
                <DashboardTintedCard
                  key={loc.location_id} tone={tone} interactive
                  onClick={() => setLocationId(loc.location_id)}
                  padding={1.75}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: t.accent }}>{loc.location_name}</Typography>
                    {loc.late > 0
                      ? <Chip label={`${loc.late} late`} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#FFFFFFCC", color: c.red, border: `1px solid ${alpha(c.red, 0.24)}` }} />
                      : <Chip label="On Track" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#FFFFFFCC", color: toneMap.green.accent, border: `1px solid ${toneMap.green.iconBg}44` }} />}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.75 }}>
                    <Box>
                      <Typography sx={{ fontSize: 20, fontWeight: 800, color: t.accent, lineHeight: 1 }}>{loc.classes}</Typography>
                      <Typography sx={{ fontSize: 10.5, color: t.accent, opacity: 0.75, fontWeight: 700, mt: 0.25, textTransform: "uppercase", letterSpacing: "0.04em" }}>Classes</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 20, fontWeight: 800, color: t.accent, lineHeight: 1 }}>{loc.seats}</Typography>
                      <Typography sx={{ fontSize: 10.5, color: t.accent, opacity: 0.75, fontWeight: 700, mt: 0.25, textTransform: "uppercase", letterSpacing: "0.04em" }}>Students</Typography>
                    </Box>
                    {loc.running > 0 && (
                      <Box>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: toneMap.green.accent, lineHeight: 1 }}>{loc.running}</Typography>
                        <Typography sx={{ fontSize: 10.5, color: toneMap.green.accent, opacity: 0.85, fontWeight: 700, mt: 0.25, textTransform: "uppercase", letterSpacing: "0.04em" }}>Running</Typography>
                      </Box>
                    )}
                  </Box>
                </DashboardTintedCard>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ══════════ Live Now + Starting Soon ══════════ */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {/* Live Now — full solid emerald fill (spec §12). */}
        <Box sx={{ bgcolor: toneMap.emerald.grad, border: `1px solid ${toneMap.emerald.border}`, borderRadius: `${dashTokens.radius.md}px`, boxShadow: dashTokens.shadow.card, overflow: "hidden" }}>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1, px: 2.5, py: 1.5,
            borderBottom: `1px solid ${c.border}`,
            background: toneMap.mint.grad,
          }}>
            <Box sx={{ width: 30, height: 30, borderRadius: "10px", bgcolor: toneMap.mint.iconBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 17 }, boxShadow: `0 4px 12px ${toneMap.mint.iconBg}40` }}>
              <BoltIcon />
            </Box>
            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: toneMap.mint.accent }}>Live Now</Typography>
            <Chip label={live.length} size="small" sx={{ ml: "auto", height: 22, fontSize: 11.5, fontWeight: 700, bgcolor: "#FFFFFFCC", color: toneMap.mint.accent, border: `1px solid ${toneMap.mint.iconBg}44` }} />
          </Box>
          <Box>
            {live.length === 0 ? (
              <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                <Box sx={{ display: "inline-flex", width: 44, height: 44, borderRadius: "14px", background: toneMap.mint.grad, color: toneMap.mint.accent, alignItems: "center", justifyContent: "center", mb: 1 }}>
                  <CheckCircleOutlineIcon />
                </Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: toneMap.mint.accent }}>
                  No classes are currently in progress.
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: c.muted, mt: 0.5 }}>
                  Live sessions will appear here when a teacher starts a class.
                </Typography>
              </Box>
            ) : live.slice(0, 5).map((o) => {
              const s = summarizeClass(o.grade, o.subject);
              return (
                <Box key={o.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.25, borderBottom: `1px solid ${c.border}`, "&:last-child": { borderBottom: "none" } }}>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: "50%", bgcolor: c.green, flexShrink: 0,
                    animation: "livePulse 1.5s infinite",
                    "@keyframes livePulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.35 } },
                  }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tooltip title={s.isComplex ? s.detail : ""} placement="top" arrow>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.compact}
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: 11, color: c.muted }}>
                      <Box component="span" sx={{ fontWeight: 800, color: c.text }}>{o.staff_name || "Staff"}</Box>
                      {" · "}{o.location_name || "—"} · {o.elapsed_minutes ?? 0}m elapsed
                    </Typography>
                  </Box>
                  <Chip label={`${o.student_count} ${o.student_count === 1 ? "Student" : "Students"}`} size="small" clickable onClick={() => openRoster(o.id)}
                    sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: c.surfaceGrey, color: c.text }} />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Starting Soon — full solid blue/cyan fill (spec §13). */}
        <Box sx={{ bgcolor: toneMap.cyan.grad, border: `1px solid ${toneMap.cyan.border}`, borderRadius: `${dashTokens.radius.md}px`, boxShadow: dashTokens.shadow.card, overflow: "hidden" }}>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1, px: 2.5, py: 1.5,
            borderBottom: `1px solid ${c.border}`,
            background: toneMap.blue.grad,
          }}>
            <Box sx={{ width: 30, height: 30, borderRadius: "10px", bgcolor: toneMap.blue.iconBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 17 }, boxShadow: `0 4px 12px ${toneMap.blue.iconBg}40` }}>
              <AccessTimeIcon />
            </Box>
            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: toneMap.blue.accent }}>Starting Soon</Typography>
            <Chip label={upcoming.length} size="small" sx={{ ml: "auto", height: 22, fontSize: 11.5, fontWeight: 700, bgcolor: "#FFFFFFCC", color: toneMap.blue.accent, border: `1px solid ${toneMap.blue.iconBg}44` }} />
          </Box>
          <Box>
            {upcoming.length === 0 ? (
              <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                <Box sx={{ display: "inline-flex", width: 44, height: 44, borderRadius: "14px", background: toneMap.blue.grad, color: toneMap.blue.accent, alignItems: "center", justifyContent: "center", mb: 1 }}>
                  <AccessTimeIcon />
                </Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: toneMap.blue.accent }}>
                  Nothing starting within the next hour.
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: c.muted, mt: 0.5 }}>
                  Upcoming classes in the next 60 minutes will appear here.
                </Typography>
              </Box>
            ) : upcoming.slice(0, 5).map((o) => {
              const s = summarizeClass(o.grade, o.subject);
              return (
                <Box key={o.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.25, borderBottom: `1px solid ${c.border}`, "&:last-child": { borderBottom: "none" } }}>
                  <Box sx={{ minWidth: 60, textAlign: "center", bgcolor: c.surfaceBlue, borderRadius: "8px", px: 0.75, py: 0.5 }}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: c.blue, lineHeight: 1 }}>{fmt12(o.start_time)}</Typography>
                    <Typography sx={{ fontSize: 9.5, color: c.blue, fontWeight: 700, mt: 0.25 }}>in {o.starts_in_minutes}m</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tooltip title={s.isComplex ? s.detail : ""} placement="top" arrow>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.compact}
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: 11, color: c.muted }}>
                      <Box component="span" sx={{ fontWeight: 800, color: c.text }}>{o.staff_name || "Staff"}</Box>
                      {" · "}{o.location_name || "—"}
                    </Typography>
                  </Box>
                  <Chip label={`${o.student_count} ${o.student_count === 1 ? "Student" : "Students"}`} size="small" clickable onClick={() => openRoster(o.id)}
                    sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: c.surfaceGrey, color: c.text }} />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ══════════ Class Subjects modal ══════════
          Opens from the "View Subjects" button on any operations row.
          Renders the structured grade→subject mapping the backend
          batches into every occurrence DTO. Falls back to a compact
          two-list view if only the flat snapshot strings are available.
          NEVER a toast/snackbar — subject data is content, not a
          notification. */}
      <Dialog
        open={!!subjectsOccId}
        onClose={() => setSubjectsOccId(null)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        {subjectsOccurrence && (() => {
          const s = summarizeClass(subjectsOccurrence.grade, subjectsOccurrence.subject);
          const gs: { grade_id: number; grade_name: string; subjects: { id: number; name: string }[] }[] =
            subjectsOccurrence.grade_subjects || [];
          const gCount = gs.length || (s.compact.split("·").length > 1 ? 0 : 1);
          const sCount = gs.reduce((n, g) => n + g.subjects.length, 0);
          // Muted steel/indigo family only — grade chips no longer
          // implied random semantic states (green/red/pink).
          const gradeChipTints = [
            { bg: "#F2F6FA", fg: "#4E78A6", border: "#D5E1EA" },
            { bg: "#EEF2F7", fg: "#17324D", border: "#D5DEEB" },
            { bg: "#F5F6FB", fg: "#6878A6", border: "#DDE1EF" },
            { bg: "#F0F4F8", fg: "#557AA1", border: "#D2DDE7" },
            { bg: "#F3F5F9", fg: "#6273A5", border: "#DBE0EE" },
            { bg: "#EDF2F7", fg: "#446A91", border: "#D0DBE7" },
          ];
          return (
            <>
              <DialogTitle sx={{ px: 3, py: 2, borderBottom: `1px solid ${c.border}` }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: "12px",
                    bgcolor: c.surfaceMint, color: c.deepGreen,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    "& svg": { fontSize: 22 }, flexShrink: 0,
                  }}>
                    <MenuBookOutlinedIcon />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: c.text, lineHeight: 1.2 }}>
                      Class Subjects
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: c.muted, mt: 0.25 }}>
                      {gs.length > 0
                        ? `${gs.length} Grade${gs.length === 1 ? "" : "s"} · ${sCount} Subject${sCount === 1 ? "" : "s"}`
                        : s.compact}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setSubjectsOccId(null)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ px: 3, py: 2.5 }}>
                {/* Context strip */}
                <Box sx={{
                  display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                  gap: 1.5, px: 1.5, py: 1.5, mb: 2.5,
                  bgcolor: c.surfaceGrey, borderRadius: "12px",
                }}>
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Teacher</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: c.text, mt: 0.25 }}>
                      {subjectsOccurrence.staff_name || "Unassigned"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Location</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.text, mt: 0.25 }}>
                      {subjectsOccurrence.location_name || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Scheduled</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.text, mt: 0.25 }}>
                      {fmt12(subjectsOccurrence.start_time)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Students</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: c.text, mt: 0.25 }}>
                      {subjectsOccurrence.student_count}
                    </Typography>
                  </Box>
                </Box>

                {/* Grade → Subject mapping */}
                {gs.length > 0 ? (
                  <Stack spacing={1.25}>
                    {gs.map((g, idx) => {
                      const tint = gradeChipTints[idx % gradeChipTints.length];
                      return (
                        <Box key={g.grade_id} sx={{
                          display: "flex", alignItems: "center", gap: 1.5,
                          p: 1.5, borderRadius: "12px",
                          bgcolor: c.card, border: `1px solid ${c.border}`,
                        }}>
                          <Box sx={{
                            minWidth: 84, px: 1, py: 0.5,
                            bgcolor: tint.bg, color: tint.fg,
                            border: `1px solid ${tint.border}`,
                            borderRadius: "8px",
                            textAlign: "center", flexShrink: 0,
                          }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>{g.grade_name}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, flex: 1, minWidth: 0 }}>
                            {g.subjects.length === 0 ? (
                              <Typography sx={{ fontSize: 11.5, color: c.muted, fontStyle: "italic" }}>No subjects mapped</Typography>
                            ) : g.subjects.map((sub) => (
                              <Chip key={sub.id} label={sub.name} size="small"
                                sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: tint.bg, color: tint.fg, border: `1px solid ${tint.border}` }} />
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box sx={{ py: 3, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: c.text, mb: 0.5 }}>
                      No grade-subject mapping available
                    </Typography>
                    {subjectsOccurrence.grade || subjectsOccurrence.subject ? (
                      <>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.06em", mt: 2 }}>
                          Legacy class label
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: c.muted, mt: 0.5 }}>
                          {[subjectsOccurrence.grade, subjectsOccurrence.subject].filter(Boolean).join(" — ")}
                        </Typography>
                      </>
                    ) : (
                      <Typography sx={{ fontSize: 12.5, color: c.muted }}>
                        This class has no linked grades or subjects.
                      </Typography>
                    )}
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${c.border}` }}>
                <Button
                  onClick={() => setSubjectsOccId(null)}
                  sx={{ textTransform: "none", borderRadius: "10px" }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  startIcon={<FactCheckOutlinedIcon2 sx={{ fontSize: "16px !important" }} />}
                  onClick={() => {
                    // Consistent with the row action: open the exact
                    // occurrence's attendance page, never the recurring
                    // class edit.
                    navigate(`/class-sessions/${subjectsOccurrence.id}/attendance`);
                    setSubjectsOccId(null);
                  }}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: c.deepGreen, boxShadow: "none", "&:hover": { bgcolor: "#3D6089", boxShadow: "none" } }}
                >
                  Open Attendance
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ══════════ View All Alerts drawer ══════════
          Full list of late-start alerts (all N rows). Search-scoped to
          the current dashboard location filter — no separate fetch. */}
      <Drawer anchor="right" open={showAllAlerts} onClose={() => setShowAllAlerts(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 } } }}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{
            px: 2.5, py: 2, borderBottom: `1px solid ${c.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
            bgcolor: c.surfaceRed,
          }}>
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.red, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                All Late-Start Alerts
              </Typography>
              <Typography sx={{ fontSize: 17, fontWeight: 800, color: c.red, mt: 0.25 }}>
                {alerts.length} class{alerts.length === 1 ? "" : "es"} not started
              </Typography>
              <Typography sx={{ fontSize: 11, color: c.red, opacity: 0.85, mt: 0.25 }}>
                Sorted by most overdue · {graceMinutes}-min grace · scope: {activeLocationName}
              </Typography>
            </Box>
            <IconButton onClick={() => setShowAllAlerts(false)} size="small"><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {alerts.map((a) => renderAlertRow(a, /*compact*/ true))}
            {alerts.length === 0 && (
              <Box sx={{ px: 3, py: 6, textAlign: "center", color: c.muted, fontSize: 13 }}>
                No late-start alerts.
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* ══════════ Students Expected roster drawer ══════════ */}
      <Drawer anchor="right" open={!!rosterOccId} onClose={() => setRosterOccId(null)} PaperProps={{ sx: { width: { xs: "100%", sm: 400 } } }}>
        {rosterOccurrence && (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Students Expected
              </Typography>
              <IconButton size="small" onClick={() => setRosterOccId(null)}><CloseIcon /></IconButton>
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.5 }}>
              {[rosterOccurrence.grade, rosterOccurrence.subject].filter(Boolean).join(" — ") || "Class"}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: c.muted, mb: 2 }}>
              {rosterOccurrence.location_name || "—"} · {fmt12(rosterOccurrence.start_time)} – {fmt12(rosterOccurrence.end_time)}
              {rosterOccurrence.staff_name ? ` · ${rosterOccurrence.staff_name}` : ""}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {rosterLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={20} /></Box>
            ) : roster.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: c.muted, textAlign: "center", py: 3 }}>
                No students enrolled in this class.
              </Typography>
            ) : (
              <Stack spacing={0.75}>
                {roster.map((s) => (
                  <Box key={s.student_id} sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1, py: 0.75, borderRadius: "8px", "&:hover": { bgcolor: c.surfaceGrey } }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: c.surfaceMint, color: c.deepGreen, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <ChildCareOutlinedIcon sx={{ fontSize: 14 }} />
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{s.name}</Typography>
                    {s.attendance_status && (
                      <Chip label={s.attendance_status} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, textTransform: "capitalize", bgcolor: c.surfaceGrey }} />
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default OperationsCommandCenter;
